import { DataSource } from "@angular/cdk/table";
import { CollectionViewer } from "@angular/cdk/collections";
import { Observable, BehaviorSubject, of } from "rxjs";
import { ToastService } from "src/app/services/toast.service";
import { IGridService } from "./grid.service";
import { finalize, catchError } from "rxjs/operators";
import { HttpHeaders } from "@angular/common/http";
import { GridLoadParams } from "./baseGrid.component";
import { GridModel } from "./grid.model";
import { NestedFilterService, NestedFilterRule } from "./nested-filter.service";

export class GridDataSource<T> implements DataSource<T> {
    public entitySubject = new BehaviorSubject<T[]>([]);
    private countSubject = new BehaviorSubject<number>(0);
    private loadingSubject = new BehaviorSubject<boolean>(true);

    public count$ = this.countSubject.asObservable();
    public isLoading$ = this.loadingSubject.asObservable();

    private nestedFilterRules: NestedFilterRule[] = [];

    constructor(
        private dataSourceService: IGridService<T>,
        private toastService: ToastService,
    ) {}

    connect(collectionViewer: CollectionViewer): Observable<T[]> {
        return this.entitySubject.asObservable();
    }

    disconnect(collectionViewer: CollectionViewer): void {
        this.entitySubject.complete();
        this.countSubject.complete();
        this.loadingSubject.complete();
    }

    load(params: GridLoadParams): void {
        this.loadingSubject.next(true);

        const headers = params.coId
            ? new HttpHeaders().set("coId", params.coId.toString())
            : undefined;
        this.dataSourceService
            .get(
                params.filter,
                params.orderBy,
                params.sort,
                params.pageIndex,
                params.pageSize,
                JSON.stringify(params.filterBy),
                headers,
            )
            .pipe(
                catchError((error) => {
                    const errorMessage =
                        error?.error?.message || "Error loading data";
                    this.toastService.displayErrorMessage(errorMessage, true);
                    return of(new GridModel<T>([], 0));
                }),
                finalize(() => this.loadingSubject.next(false)),
            )
            .subscribe((dataSourceModel) => {
                // Apply nested filtering if rules are configured
                const processedData =
                    this.nestedFilterRules.length > 0
                        ? NestedFilterService.getInstance().processNestedFilters(
                              dataSourceModel.data,
                              params.filterBy,
                              this.nestedFilterRules,
                          )
                        : dataSourceModel.data;

                this.entitySubject.next(processedData);
                this.countSubject.next(dataSourceModel.count);
            });
    }

    /**
     * Set nested filter rules for processing nested object filtering
     */
    setNestedFilterRules(rules: NestedFilterRule[]): void {
        this.nestedFilterRules = rules || [];
    }

    getData(): T[] {
        return this.entitySubject.getValue();
    }

    getCount(): number {
        return this.countSubject.getValue();
    }

    updateData(data: T[]): void {
        this.entitySubject.next(data);
    }

    setCount(count: number): void {
        this.countSubject.next(count);
    }
}
